import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { useUserAuthStore } from '../store/userAuthStore'
import { useApiDebuggerStore } from '../store/apiDebuggerStore'

const DEFAULT_USER_API_BASE_URL = 'https://api.waybook.me'

const USER_API_BASE_URL = import.meta.env.VITE_USER_API_BASE_URL || DEFAULT_USER_API_BASE_URL

type RequestMetadata = {
  skipAuth?: boolean
  requestId?: string
  startedAt?: number
}

type RequestConfig = InternalAxiosRequestConfig & {
  metadata?: RequestMetadata
}

const userApi: AxiosInstance = axios.create({
  baseURL: USER_API_BASE_URL,
})

userApi.interceptors.request.use(
  (config: RequestConfig) => {
    const token = useUserAuthStore.getState().accessToken
    const debuggerStore = useApiDebuggerStore.getState()

    if (!config.headers) {
      config.headers = {} as any
    }

    if (token && !config.metadata?.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json'
    }

    const requestId = config.metadata?.requestId || crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`
    const startedAt = Date.now()

    config.metadata = {
      ...config.metadata,
      requestId,
      startedAt,
    }

    const method = config.method?.toUpperCase() ?? 'GET'
    const urlPath = config.url || '/'
    const fullUrl = config.baseURL
      ? new URL(urlPath, config.baseURL).toString()
      : urlPath

    const requestBody =
      config.data instanceof FormData
        ? '[FormData]'
        : typeof config.data === 'string'
        ? parseJsonSafe(config.data)
        : config.data

    debuggerStore.addEntry({
      id: requestId,
      method,
      url: fullUrl,
      startedAt,
      status: 'pending',
      requestBody,
    })

    return config
  },
  (error) => Promise.reject(error)
)

userApi.interceptors.response.use(
  (response) => {
    const debuggerStore = useApiDebuggerStore.getState()
    const config = response.config as RequestConfig
    const requestId = config.metadata?.requestId
    const startedAt = config.metadata?.startedAt ?? Date.now()
    const durationMs = Date.now() - startedAt

    if (requestId) {
      debuggerStore.updateEntry(requestId, {
        status: 'success',
        statusCode: response.status,
        durationMs,
        responseBody: response.data,
      })
    }

    return response
  },
  async (error: AxiosError) => {
    const debuggerStore = useApiDebuggerStore.getState()
    const config = error.config as RequestConfig | undefined
    const requestId =
      config?.metadata?.requestId || crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`
    const startedAt = config?.metadata?.startedAt ?? Date.now()
    const durationMs = Date.now() - startedAt
    const method = config?.method?.toUpperCase() ?? 'UNKNOWN'
    const url = config?.baseURL
      ? new URL(config?.url || '/', config.baseURL).toString()
      : config?.url || 'UNKNOWN'
    const requestBody =
      config?.data instanceof FormData
        ? '[FormData]'
        : typeof config?.data === 'string'
        ? parseJsonSafe(config?.data)
        : config?.data
    const errorMessage =
      (error.response?.data as any)?.error?.toString() ||
      (error.response?.data as any)?.message?.toString() ||
      error.message

    if (config?.metadata?.requestId) {
      debuggerStore.updateEntry(requestId, {
        status: 'error',
        statusCode: error.response?.status,
        durationMs,
        responseBody: error.response?.data,
        error: errorMessage,
      })
    } else {
      debuggerStore.addEntry({
        id: requestId,
        method,
        url,
        startedAt,
        status: 'error',
        statusCode: error.response?.status,
        requestBody,
        responseBody: error.response?.data,
        durationMs,
        error: errorMessage,
      })
    }

    if (error.response?.status === 401) {
      const { refreshToken, handleUnauthorized } = useUserAuthStore.getState()

      if (!refreshToken) {
        handleUnauthorized()
        return Promise.reject(error)
      }

      try {
        const refreshResponse = await userApi.post(
          '/auth/refresh',
          { refresh_token: refreshToken },
          { metadata: { skipAuth: true } } as RequestConfig
        )

        const { access_token: newAccessToken, refresh_token: newRefreshToken } = refreshResponse.data?.data || {}

        if (newAccessToken && newRefreshToken) {
          useUserAuthStore.getState().setTokens(newAccessToken, newRefreshToken)

          const originalRequest = error.config as RequestConfig
          if (originalRequest?.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          }

          return userApi(originalRequest)
        }
      } catch (refreshError) {
        console.error('Failed to refresh user token', refreshError)
        handleUnauthorized()
      }
    }

    return Promise.reject(error)
  }
)

function parseJsonSafe(payload: string) {
  try {
    return JSON.parse(payload)
  } catch {
    return payload
  }
}

export { userApi, USER_API_BASE_URL }
